/* @flow */
import Base from "nexia_mobile/platforms/base";
import Environment from "nexia_mobile/environment";
import Bridge from "nexia_mobile/platforms/ios/bridge";

import type {
  Geofence,
  NativeHTTPRequestOptions,
} from "nexia_mobile/platforms/base";
import type VognitionHomeProfile from "nexia_mobile/vognition_home_profile";

const ignoreNetworkError = () => {
  // These promises will only reject for network errors (like CORS, not like 500)
  // Ignore such problems when the function is fire-and-forget.
  // When the promise is actually used, we usually also ignore the error because
  // the issue is usually the power button closing the app. The error should be
  // handled when possible, just in case.
};

export default class IOSPlatform extends Base {
  _storedValues: { [key: string]: string };
  _storedValues = {};
  _biometricString = "fingerprint";

  initScriptInterface(scriptInterfaceCallback: () => void) {
    Promise.all([
      Bridge.localStorage.getAllStoredValues(),
      Bridge.platformAttributes.getPlatformAttributes(),
      Bridge.misc.isVoiceOverEnabled(),
    ])
      .then(([storedValues, platformAttributes, isVoiceOverEnabled]) => {
        this._storedValues = storedValues;

        this.setBundleIdentifier(platformAttributes.bundleIdentifier);
        this.setAppVersion(platformAttributes.appVersion);
        this.setPlatformName(platformAttributes.platformName);
        this.setManufacturer(platformAttributes.manufacturer);
        this.setDeviceName(platformAttributes.deviceName);
        this.setModel(platformAttributes.model);
        this.setOsVersion(platformAttributes.osVersion);
        this.setDeviceUUID(platformAttributes.deviceUUID);
        this.setCurrentLocale(platformAttributes.currentLocale);

        this.setIsScreenReaderRunning(isVoiceOverEnabled.result);

        scriptInterfaceCallback();
      })
      .catch((reason) => {
        scriptInterfaceCallback();
        throw reason; // we want this to go to Sentry
      });
  }

  allowScreenDimming(yesno: boolean = true) {
    if (yesno) {
      Bridge.misc.allowScreenDimming().catch(ignoreNetworkError);
    } else {
      Bridge.misc.keepScreenOn().catch(ignoreNetworkError);
    }
  }

  getStoredValue(key: string) {
    return this._storedValues[key];
  }

  setStoredValue(key: string, value: string) {
    this._storedValues[key] = value;
    Bridge.localStorage.setStoredValue(key, value).catch(ignoreNetworkError);
  }

  clearStoredValue(key: string) {
    delete this._storedValues[key];
    Bridge.localStorage.clearStoredValue(key).catch(ignoreNetworkError);
  }

  clearStoredValues() {
    this._storedValues = {};
    Bridge.localStorage.clearStoredValues().catch(ignoreNetworkError);
  }

  canSendFeedback() {
    return true;
  }

  obtainPushNotificationToken() {
    Bridge.misc.obtainPushNotificationToken().catch(ignoreNetworkError);
  }

  resetAppBadge() {
    Bridge.misc.resetAppBadge().catch(ignoreNetworkError);
  }

  hideSoftKeyboard() {
    Bridge.misc.hideSoftKeyboard().catch(ignoreNetworkError);
  }

  isImageSaveSupported() {
    return true;
  }

  saveImage(base64Data: string) {
    return Bridge.misc.saveImage(base64Data);
  }

  isMobilePlatform() {
    return true;
  }

  hasAppInstalled(name: string) {
    return Bridge.misc
      .hasAppInstalled(name)
      .then((response) => response.result);
  }

  sendGoogleAnalyticsEvent(category: string, action: string, label: string) {
    this.sendGoogleAnalyticsDuration(category, action, label);
  }

  sendGoogleAnalyticsDuration(
    category: string,
    action: string,
    label: string,
    value: ?number
  ) {
    Bridge.misc
      .sendGoogleAnalyticsEvent(category, action, label, value || 0)
      .catch(ignoreNetworkError);
  }

  // Geofencing methods
  isGeofencingSupported() {
    return !Environment.isCommercial();
  }

  _inflightPermissionPromise: ?Promise<boolean>;

  checkLocationPermissions() {
    if (this._inflightPermissionPromise) {
      // We can only have a single permission request at once
      return this._inflightPermissionPromise;
    }
    return (this._inflightPermissionPromise = Bridge.geofence
      .checkLocationPermissions()
      .then((response) => {
        this._inflightPermissionPromise = null;
        return response.result;
      }));
  }

  getLocationServicesWarningMessage(): Promise<string> {
    return Promise.resolve(
      "Location Services must be enabled for geofencing to function.\n\nPlease open your Settings app and go to Privacy ➔ Location Services to enable Location Services."
    );
  }

  areLocationServicesEnabled() {
    return Bridge.geofence
      .areLocationServicesEnabled()
      .then((response) => response.result);
  }

  openAppPermissionSettings() {
    Bridge.misc.openAppPermissionSettings().catch(ignoreNetworkError);
  }

  setGeofences(geofences: Array<Geofence>) {
    const filteredGeofences = geofences.map(
      ({
        id,
        name,
        latitude,
        longitude,
        radius,
        hrefAcknowledge,
        hrefEnter,
        hrefExit,
      }) => ({
        id,
        name,
        latitude,
        longitude,
        radius,
        hrefAcknowledge,
        hrefEnter,
        hrefExit,
      })
    );
    return Bridge.geofence.setGeofences(filteredGeofences).then(() => {
      /* discard response */
    });
  }

  startGeofenceService() {
    return Bridge.geofence.startGeofenceService().then(() => {
      /* discard response */
    });
  }

  stopGeofenceService() {
    return Bridge.geofence
      .stopGeofenceService()
      .then(() => {
        /* discard response */
      })
      .catch(ignoreNetworkError);
  }

  getGeofenceCrossings() {
    return Bridge.geofence
      .getGeofenceCrossings()
      .then((response) => response.crossings);
  }
  // end Geofencing methods

  // Vognition methods
  checkMicrophonePermissions(): Promise<"granted" | "denied" | "undetermined"> {
    return Bridge.vognition
      .checkMicrophonePermissions()
      .then((response) => response.status)
      .catch(() => "undetermined");
  }

  isVognitionSupported() {
    return true;
  }

  runVognitionListener() {
    Bridge.vognition.runVognitionListener().catch(ignoreNetworkError);
  }

  stopVognitionListener() {
    Bridge.vognition.stopVognitionListener().catch(ignoreNetworkError);
  }

  speakString(message: string) {
    Bridge.vognition.speakString(message).catch(ignoreNetworkError);
  }

  setVognitionHomeProfile(profile: VognitionHomeProfile): Promise<void> {
    return Bridge.vognition
      .setCurrentVognitionHomeWithProfile(
        profile.id,
        encodeURIComponent(JSON.stringify(profile))
      )
      .then(() => {
        /* discard response */
      });
  }
  // end Vognition methods

  // Biometric methods. "Fingerprint" is the most common, but FaceID also exists
  getBiometricString() {
    return this._biometricString;
  }

  isFingerprintAuthAvailable() {
    return Bridge.touchID.isTouchIDAvailable().then((response) => {
      this._biometricString = response.isActuallyFaceID
        ? "Face ID"
        : "fingerprint";
      return response.result;
    });
  }

  authenticateWithFingerprint() {
    return Bridge.touchID.authenticateWithTouchID();
  }

  cancelFingerprintAuthentication() {
    Bridge.touchID.cancelTouchIDAuthentication().catch(ignoreNetworkError);
  }
  // end Fingerprint methods

  // Native HTTP methods, to bypass CORS
  canUseNativeHTTP() {
    return true;
  }

  nativeHTTPRequest(options: NativeHTTPRequestOptions) {
    return Bridge.http.nativeHTTPRequest(options);
  }
  // end native HTTP methods

  canDo724OpenAuthEncryption(): boolean {
    return true;
  }

  encryptStringFor724OpenAuth(key: string, input: string): Promise<string> {
    return Bridge.misc
      .encryptStringFor724OpenAuth(key, input)
      .then((response) => response.result);
  }

  canDetectIfScreenReaderIsRunning() {
    return true;
  }

  createTemporaryFile(blob: Blob, extension: string): Promise<string> {
    return Bridge.temp
      .createTemporaryFile(blob, extension)
      .then((response) => response.url);
  }

  removeTemporaryFile(url: string): Promise<void> {
    return Bridge.temp.removeTemporaryFile(url).then(() => {
      /* discard response */
    });
  }
}



// WEBPACK FOOTER //
// ./src/platforms/ios/index.js